import { useEffect, useState, useRef } from "react";
import { Plus, Copy, Trash2, Image as ImageIcon, Upload, X, ArrowUp, ArrowDown } from "lucide-react";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const EMPTY = {
  name: "", price: 0, sale_price: null, category: "", collection: "", color: "", badge: "",
  short_description: "", description: "", material: "", fit: "", care: "", tags: [], sizes: [],
  variants: [], stock: 0, images: [], status: "draft", featured: false, bestseller: false,
  new_arrival: false, sku: "", seo_title: "", seo_description: "", alt_text: "",
  colors: [], variant_images: {}, fit_type: "", weight: "", composition: "",
  model_height: "", model_size: "", size_guide: [],
};

function MediaPicker({ open, onClose, onPick }) {
  const [media, setMedia] = useState([]);
  useEffect(() => { if (open) api.get("/admin/media").then(({ data }) => setMedia(data)).catch(() => {}); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-[#F7F5F1]">
        <DialogHeader><DialogTitle className="heading-x text-sm">KIES UIT MEDIABIBLIOTHEEK</DialogTitle></DialogHeader>
        {media.length === 0 ? <p className="label-x py-8 text-center">GEEN MEDIA. UPLOAD IN DE MEDIABIBLIOTHEEK.</p> : (
          <div className="grid grid-cols-4 gap-3 max-h-96 overflow-y-auto no-scrollbar">
            {media.map((m) => (
              <button key={m.id} onClick={() => { onPick(m.url); onClose(); }} className="aspect-square bg-[#E8E1D8] overflow-hidden border hover:border-[#1D1D1B]" data-testid={`media-pick-${m.id}`}>
                <img src={mediaUrl(m.url)} alt={m.alt_text} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ImageManager({ value, onChange, testid }) {
  const fileRef = useRef(null);
  const [picker, setPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const list = value || [];
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const urls = [];
    for (const file of files) {
      const fd = new FormData(); fd.append("file", file);
      try { const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); urls.push(data.url); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }
    onChange([...list, ...urls]);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= list.length) return; const c = [...list]; [c[i], c[j]] = [c[j], c[i]]; onChange(c); };
  return (
    <div>
      <div className="flex gap-2 mt-1">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/avif" multiple hidden onChange={upload} data-testid={`${testid}-file`} />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="label-x border border-[#E8E1D8] px-3 py-1.5 flex items-center gap-1" data-testid={`${testid}-upload`}><Upload size={13} /> {busy ? "UPLOADEN…" : "UPLOAD FOTO'S"}</button>
        <button type="button" onClick={() => setPicker(true)} className="label-x border border-[#E8E1D8] px-3 py-1.5 flex items-center gap-1" data-testid={`${testid}-library`}><ImageIcon size={13} /> UIT BIBLIOTHEEK</button>
      </div>
      {list.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {list.map((im, i) => (
            <div key={i} className="relative w-16 h-20 bg-[#E8E1D8] overflow-hidden group border border-[#E8E1D8]" data-testid={`${testid}-thumb-${i}`}>
              <img src={mediaUrl(im)} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-0.5 bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={() => move(i, -1)} className="text-white p-0.5" title="Naar links"><ArrowUp size={12} className="-rotate-90" /></button>
                <button type="button" onClick={() => remove(i)} className="text-white p-0.5" title="Verwijder" data-testid={`${testid}-remove-${i}`}><X size={12} /></button>
                <button type="button" onClick={() => move(i, 1)} className="text-white p-0.5" title="Naar rechts"><ArrowDown size={12} className="-rotate-90" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <MediaPicker open={picker} onClose={() => setPicker(false)} onPick={(url) => onChange([...list, url])} />
    </div>
  );
}

function ProductForm({ initial, onSaved, onClose }) {
  const [f, setF] = useState({ ...EMPTY, ...initial, tags: initial?.tags || [], images: initial?.images || [], sizes: initial?.sizes || [], colors: initial?.colors || [], variant_images: initial?.variant_images || {}, size_guide: initial?.size_guide || [] });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const toggleSize = (s) => {
    const has = f.sizes.includes(s);
    const sizes = has ? f.sizes.filter((x) => x !== s) : [...f.sizes, s];
    let variants = f.variants.filter((v) => sizes.includes(v.size));
    sizes.forEach((sz) => { if (!variants.find((v) => v.size === sz)) variants.push({ size: sz, sku: `${f.sku || "SKU"}-${sz}`, stock: 0, price: null, sale_price: null }); });
    setF((p) => ({ ...p, sizes, variants }));
  };
  const setVariantStock = (size, stock) => set("variants", f.variants.map((v) => v.size === size ? { ...v, stock: parseInt(stock) || 0 } : v));
  const [colorInput, setColorInput] = useState("");
  const addColor = () => { const c = colorInput.trim(); if (!c || f.colors.includes(c)) return; setF((p) => ({ ...p, colors: [...p.colors, c] })); setColorInput(""); };
  const removeColor = (c) => setF((p) => { const vi = { ...(p.variant_images || {}) }; delete vi[c]; return { ...p, colors: p.colors.filter((x) => x !== c), variant_images: vi }; });
  const setColorImages = (c, v) => setF((p) => ({ ...p, variant_images: { ...(p.variant_images || {}), [c]: v } }));
  const addSizeRow = () => set("size_guide", [...(f.size_guide || []), { size: "", chest: "", length: "" }]);
  const setSizeRow = (i, k, v) => set("size_guide", (f.size_guide || []).map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const removeSizeRow = (i) => set("size_guide", (f.size_guide || []).filter((_, idx) => idx !== i));

  const save = async () => {
    const payload = { ...f, price: parseFloat(f.price) || 0, sale_price: f.sale_price ? parseFloat(f.sale_price) : null,
      stock: f.variants.length ? f.variants.reduce((s, v) => s + (v.stock || 0), 0) : parseInt(f.stock) || 0,
      badge: f.badge || null };
    try {
      if (initial?.id) await api.put(`/admin/products/${initial.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Product opgeslagen");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const inp = "w-full bg-white border border-[#E8E1D8] px-3 py-2 text-sm outline-none focus:border-[#1D1D1B]";
  return (
    <div className="space-y-4" data-testid="product-form">
      <div className="grid grid-cols-2 gap-3">
        <label className="col-span-2"><span className="label-x">NAAM</span><input className={inp} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="pf-name" /></label>
        <label><span className="label-x">SKU</span><input className={inp} value={f.sku} onChange={(e) => set("sku", e.target.value)} data-testid="pf-sku" /></label>
        <label><span className="label-x">KLEUR</span><input className={inp} value={f.color} onChange={(e) => set("color", e.target.value)} data-testid="pf-color" /></label>
        <label><span className="label-x">PRIJS (€)</span><input type="number" step="0.01" className={inp} value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="pf-price" /></label>
        <label><span className="label-x">ACTIEPRIJS (€)</span><input type="number" step="0.01" className={inp} value={f.sale_price || ""} onChange={(e) => set("sale_price", e.target.value)} data-testid="pf-sale" /></label>
        <label><span className="label-x">CATEGORIE</span><input className={inp} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="pf-category" /></label>
        <label><span className="label-x">COLLECTIE (slug)</span><input className={inp} value={f.collection} onChange={(e) => set("collection", e.target.value)} data-testid="pf-collection" /></label>
        <label><span className="label-x">BADGE</span><input className={inp} value={f.badge || ""} onChange={(e) => set("badge", e.target.value)} placeholder="NIEUW / SALE" data-testid="pf-badge" /></label>
        <label><span className="label-x">TAGS (komma)</span><input className={inp} value={f.tags.join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} data-testid="pf-tags" /></label>
      </div>
      <label className="block"><span className="label-x">KORTE OMSCHRIJVING</span><input className={inp} value={f.short_description} onChange={(e) => set("short_description", e.target.value)} data-testid="pf-short" /></label>
      <label className="block"><span className="label-x">OMSCHRIJVING</span><textarea rows={3} className={inp} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="pf-desc" /></label>
      <div className="grid grid-cols-3 gap-3">
        <label><span className="label-x">MATERIAAL</span><input className={inp} value={f.material || ""} onChange={(e) => set("material", e.target.value)} /></label>
        <label><span className="label-x">FIT (tekst)</span><input className={inp} value={f.fit || ""} onChange={(e) => set("fit", e.target.value)} /></label>
        <label><span className="label-x">ONDERHOUD</span><input className={inp} value={f.care || ""} onChange={(e) => set("care", e.target.value)} /></label>
        <label><span className="label-x">FIT-TYPE</span>
          <select className={inp} value={f.fit_type || ""} onChange={(e) => set("fit_type", e.target.value)} data-testid="pf-fit-type">
            <option value="">—</option><option>Regular Fit</option><option>Oversized</option><option>Relaxed Fit</option><option>Slim Fit</option>
          </select></label>
        <label><span className="label-x">GEWICHT</span><input className={inp} value={f.weight || ""} onChange={(e) => set("weight", e.target.value)} placeholder="bijv. 480 GSM" /></label>
        <label><span className="label-x">SAMENSTELLING</span><input className={inp} value={f.composition || ""} onChange={(e) => set("composition", e.target.value)} placeholder="100% katoen" /></label>
        <label><span className="label-x">MODEL LENGTE</span><input className={inp} value={f.model_height || ""} onChange={(e) => set("model_height", e.target.value)} placeholder="bijv. 186 cm" /></label>
        <label><span className="label-x">MODEL DRAAGT MAAT</span><input className={inp} value={f.model_size || ""} onChange={(e) => set("model_size", e.target.value)} placeholder="bijv. L" /></label>
      </div>
      <div>
        <div className="flex items-center justify-between"><span className="label-x">MAATTABEL (cm)</span>
          <button type="button" onClick={addSizeRow} className="label-x flex items-center gap-1" data-testid="pf-add-sizerow"><Plus size={12} /> RIJ</button></div>
        {(f.size_guide || []).length > 0 && (
          <div className="mt-2 space-y-2">
            {(f.size_guide || []).map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                <input className={inp} value={r.size || ""} onChange={(e) => setSizeRow(i, "size", e.target.value)} placeholder="Maat (S)" />
                <input className={inp} value={r.chest || ""} onChange={(e) => setSizeRow(i, "chest", e.target.value)} placeholder="Borst" />
                <input className={inp} value={r.length || ""} onChange={(e) => setSizeRow(i, "length", e.target.value)} placeholder="Lengte" />
                <button type="button" onClick={() => removeSizeRow(i)} className="text-[#9B1C1C]" title="Verwijder rij"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <span className="label-x">MATEN & VOORRAAD</span>
        <div className="flex flex-wrap gap-2 mt-2">
          {SIZES.map((s) => <button key={s} type="button" onClick={() => toggleSize(s)} className={`px-3 py-1.5 text-xs font-mono border ${f.sizes.includes(s) ? "bg-[#1D1D1B] text-[#F7F5F1]" : "border-[#E8E1D8]"}`} data-testid={`pf-size-${s}`}>{s}</button>)}
        </div>
        {f.variants.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mt-3">
            {f.variants.map((v) => (
              <div key={v.size} className="flex items-center gap-2 border border-[#E8E1D8] bg-white px-2 py-1">
                <span className="font-mono text-xs w-8">{v.size}</span>
                <input type="number" value={v.stock} onChange={(e) => setVariantStock(v.size, e.target.value)} className="w-full text-xs outline-none" placeholder="voorraad" data-testid={`pf-stock-${v.size}`} />
              </div>
            ))}
          </div>
        )}
        {f.variants.length === 0 && <label className="block mt-3"><span className="label-x">VOORRAAD (geen maten)</span><input type="number" className={inp} value={f.stock} onChange={(e) => set("stock", e.target.value)} data-testid="pf-stock" /></label>}
      </div>
      <div>
        <span className="label-x">FOTO'S (HOOFD)</span>
        <ImageManager value={f.images} onChange={(v) => set("images", v)} testid="pf-images" />
      </div>
      <div>
        <span className="label-x">KLEUREN & FOTO'S PER KLEUR</span>
        <div className="flex gap-2 mt-1">
          <input className={inp} value={colorInput} onChange={(e) => setColorInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addColor(); } }} placeholder="Kleur (bijv. Zwart)" data-testid="pf-color-input" />
          <button type="button" onClick={addColor} className="label-x border border-[#E8E1D8] px-3 py-1.5 whitespace-nowrap flex items-center gap-1" data-testid="pf-add-color"><Plus size={13} /> KLEUR</button>
        </div>
        {f.colors.map((col) => (
          <div key={col} className="border border-[#E8E1D8] bg-white p-3 mt-2" data-testid={`pf-color-block-${col}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium">{col}</span>
              <button type="button" onClick={() => removeColor(col)} className="text-[#9B1C1C]" title="Kleur verwijderen"><Trash2 size={14} /></button>
            </div>
            <ImageManager value={f.variant_images?.[col] || []} onChange={(v) => setColorImages(col, v)} testid={`pf-cimg-${col}`} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label><span className="label-x">SEO-TITEL</span><input className={inp} value={f.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} /></label>
        <label><span className="label-x">SEO-OMSCHRIJVING</span><input className={inp} value={f.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} /></label>
      </div>
      <div className="flex flex-wrap gap-4 items-center">
        <label className="flex items-center gap-2 label-x"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="pf-featured" /> UITGELICHT</label>
        <label className="flex items-center gap-2 label-x"><input type="checkbox" checked={f.new_arrival} onChange={(e) => set("new_arrival", e.target.checked)} /> NIEUW</label>
        <label className="flex items-center gap-2 label-x"><input type="checkbox" checked={f.bestseller} onChange={(e) => set("bestseller", e.target.checked)} /> BESTSELLER</label>
        <select value={f.status} onChange={(e) => set("status", e.target.value)} className="border border-[#E8E1D8] bg-white px-3 py-1.5 text-xs font-mono ml-auto" data-testid="pf-status">
          <option value="draft">CONCEPT</option><option value="published">GEPUBLICEERD</option><option value="archived">GEARCHIVEERD</option>
        </select>
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={save} className="btn-x" data-testid="pf-save">PRODUCT OPSLAAN</button>
        <button onClick={onClose} className="btn-x-outline">ANNULEER</button>
      </div>
    </div>
  );
}

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/products").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const dup = async (id) => { await api.post(`/admin/products/${id}/duplicate`); toast.success("Gedupliceerd"); load(); };
  const del = async (id) => { if (!window.confirm("Product verwijderen?")) return; await api.delete(`/admin/products/${id}`); toast.success("Verwijderd"); load(); };
  const setStatus = async (id, status) => { await api.patch(`/admin/products/${id}/status`, { status }); load(); };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6">
        <h1 className="heading-x text-2xl">PRODUCTEN</h1>
        <div className="flex gap-3">
          <a href={`${api.defaults.baseURL}/admin/export/products.csv`} className="btn-x-outline text-[0.65rem]" data-testid="export-products">EXPORT CSV</a>
          <button onClick={() => setEditing({})} className="btn-x text-[0.65rem]" data-testid="new-product-btn"><Plus size={14} /> NIEUW PRODUCT</button>
        </div>
      </div>
      <div className="border border-[#E8E1D8] bg-white divide-y divide-[#E8E1D8]">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-4 p-3" data-testid={`admin-product-${p.id}`}>
            <div className="w-12 h-16 bg-[#E8E1D8] overflow-hidden shrink-0">{p.images?.[0] && <img src={mediaUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{p.name} {p.demo && <span className="label-x">DEMO</span>}</p>
              <p className="label-x mt-0.5">€{p.price?.toFixed(2)} · VOORRAAD {p.stock} · {p.category}</p>
            </div>
            <select value={p.status} onChange={(e) => setStatus(p.id, e.target.value)} className="border border-[#E8E1D8] bg-white px-2 py-1 text-xs font-mono" data-testid={`status-${p.id}`}>
              <option value="draft">CONCEPT</option><option value="published">GEPUBLICEERD</option><option value="archived">GEARCHIVEERD</option>
            </select>
            <button onClick={() => setEditing(p)} className="label-x" data-testid={`edit-${p.id}`}>BEWERK</button>
            <button onClick={() => dup(p.id)} title="Dupliceer"><Copy size={15} /></button>
            <button onClick={() => del(p.id)} title="Verwijder"><Trash2 size={15} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="label-x p-8 text-center">GEEN PRODUCTEN</p>}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl bg-[#F7F5F1] max-h-[92vh] overflow-y-auto no-scrollbar">
          <DialogHeader><DialogTitle className="heading-x text-sm">{editing?.id ? "PRODUCT BEWERKEN" : "NIEUW PRODUCT"}</DialogTitle></DialogHeader>
          {editing && <ProductForm initial={editing} onSaved={() => { setEditing(null); load(); }} onClose={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
